/**
 * Redux Action Types and Creators
 * Defines the action types and creators for the questionnaire state management
 */

import { questionType } from "../types";

// Optional audio example payload stored with results
export type UserExampleAudio = {
  dataUrl: string;      // base64 data URL (e.g., audio/webm)
  mimeType: string;     // e.g., "audio/webm"
  durationMs: number;   // duration in milliseconds
  createdAt: string;    // ISO timestamp
};

// Child information interface
export interface ChildInfoData {
  guardianName: string;
  guardianPhone: string;
  childName: string;
  gender: "male" | "female";
  dateOfBirth: string;
  city: string;
  pincode: string;
}

// Action type definitions
export type ActionTypes = 
  | { type: "SAVE_QUESTION_RESULT"; payload: { questionId: number; result: "pass" | "fail"; mainAnswer: "yes" | "no"; subAnswers: ("yes" | "no" | "zero" | "one" | "normal" | "below-normal" | "inconclusive")[]; mostOften?: "zero" | "one"; userExample?: string; noExamplesChecked?: boolean; examplesSaved?: boolean; userExampleAudio?: UserExampleAudio } }
  | { type: "SAVE_COMPLEX_QUESTION_RESULT"; payload: { questionId: number; result: "pass" | "fail"; complexData: any } }
  | { type: "CLEAR_QUESTION_RESULT"; payload: { questionId: number } }
  | { type: "SET_QUESTION_COMPLETED"; payload: { questionId: number; completed: boolean } }
  | { type: "SAVE_CHILD_INFO"; payload: ChildInfoData }
  | { type: "CLEAR_CHILD_INFO"; payload: void }
  | { type: "CLEAR_ALL_DATA"; payload: void };

/**
 * Action creator for saving individual question results
 * @param questionId - Question ID (1-20)
 * @param result - Pass or fail result
 * @param mainAnswer - Main yes/no answer
 * @param subAnswers - Array of sub-question answers (includes hearing test values for question 2)
 * @param mostOften - Which behavior type is most often (optional)
 * @param userExample - User's text example (optional)
 */
export const saveQuestionResult = (
  questionId: number, 
  result: "pass" | "fail", 
  mainAnswer: "yes" | "no", 
  subAnswers: ("yes" | "no" | "zero" | "one" | "normal" | "below-normal" | "inconclusive")[],
  mostOften?: "zero" | "one",
  userExample?: string,
  noExamplesChecked?: boolean,
  examplesSaved?: boolean,
  userExampleAudio?: UserExampleAudio
): ActionTypes => ({
  type: "SAVE_QUESTION_RESULT",
  payload: { questionId, result, mainAnswer, subAnswers, mostOften, userExample, noExamplesChecked, examplesSaved, userExampleAudio }
});

/**
 * Action creator for saving complex question results (like Question18)
 * @param questionId - Question ID (1-20)
 * @param result - Pass or fail result
 * @param complexData - Complex data object with specific question properties
 */
export const saveComplexQuestionResult = (
  questionId: number, 
  result: "pass" | "fail", 
  complexData: any
): ActionTypes => ({
  type: "SAVE_COMPLEX_QUESTION_RESULT",
  payload: { questionId, result, complexData }
});

/**
 * Action creator for clearing a question result
 * @param questionId - Question ID to clear
 */
export const clearQuestionResult = (questionId: number): ActionTypes => ({
  type: "CLEAR_QUESTION_RESULT",
  payload: { questionId }
});

/**
 * Action creator for marking a question as completed
 * @param questionId - Question ID
 * @param completed - Whether the question is completed
 */
export const setQuestionCompleted = (questionId: number, completed: boolean): ActionTypes => ({
  type: "SET_QUESTION_COMPLETED",
  payload: { questionId, completed }
});

/**
 * Action creator for saving child information
 * @param childInfo - Child and guardian information
 */
export const saveChildInfo = (childInfo: ChildInfoData): ActionTypes => ({
  type: "SAVE_CHILD_INFO",
  payload: childInfo
});

/**
 * Action creator for clearing child information
 */
export const clearChildInfo = (): ActionTypes => ({
  type: "CLEAR_CHILD_INFO",
  payload: undefined
});

/**
 * Action creator for clearing all assessment data (child info and question results)
 */
export const clearAllData = (): ActionTypes => ({
  type: "CLEAR_ALL_DATA",
  payload: undefined
});